import Grid from '@mui/material/Grid';

import { MetricBand, MetricTile } from '~/components/common/MetricTile';
import type { KPISummary } from '~/data/generated/model';
import { callbackTypeValues, type EntryTypeKey } from '~/data/schemas/ticket';
import { useLocaleTerms } from '~/hooks/useLocaleTerms';
import getKpiFilter, { AvailableKpis, type AvailableKpisType } from '~/lib/portfolio/getKpiFilter';
import { formatDecimalStrict, formatNumber, formatPercent } from '~/utils/format';
import { buildUrlForPropertyTickets, buildUrlForTickets } from '~/utils/urlBuilder';

export type PortfolioPerformanceBandProps = {
  summary: KPISummary | null;
  kpisToShow?: AvailableKpisType[];
  propertyId?: string;
  /**
   * Drops the {@link MetricBand} card and renders the tiles as a bare row, for a caller
   * that already has a surface to sit them on — the property page's figures are the
   * bottom of its tinted header band, and a white card would cut that band in two.
   */
  bare?: boolean;
  /** Fixed tiles per row, for a surface narrower than the viewport (the row preview). */
  perRow?: number;
};

/**
 * The dashboard's summary strip: six performance figures in the shared
 * {@link MetricBand}, in place of four stacked cards that filled half the first screen.
 * No tile takes the accent — a portfolio's health is the shape of all six together.
 */
export function PortfolioPerformanceBand({
  summary,
  kpisToShow = [
    AvailableKpis.maintenanceCompletion,
    AvailableKpis.maintenanceVisit,
    AvailableKpis.emergencyCalls,
    AvailableKpis.callbacks,
  ],
  propertyId,
  bare = false,
  perRow,
}: PortfolioPerformanceBandProps) {
  const terms = useLocaleTerms();
  const showKpi = getKpiFilter(kpisToShow);

  // The same destinations the legacy cards linked to: a property scopes its
  // links to that property, the portfolio links to the whole ticket list.
  const ticketsPath = (entryType: EntryTypeKey[]) =>
    propertyId
      ? buildUrlForPropertyTickets({ propertyId, entryType })
      : buildUrlForTickets({ entryType });

  const visitTicketsPath = ticketsPath(['visit']);
  const emergencyTicketsPath = ticketsPath(['emergency']);
  const callbacksPath = ticketsPath(callbackTypeValues);

  const unit = terms.unit.toLowerCase();

  // `AvailableKpis.emergencyCalls` and `.callbacks` each gated a two-metric card in the
  // old layout, so each still gates a pair of tiles here.
  const tiles = [
    {
      show: showKpi(AvailableKpis.maintenanceCompletion),
      testId: 'maintenance-completion',
      label: 'Maintenance Completion',
      value: formatPercent(summary?.maintenanceCompletion ?? null),
      href: visitTicketsPath,
      info: 'Maintenance Completion represents the percentage of contractually required preventive maintenance visits for each elevator actually performed.',
      dataShot: 'portfolio-maintenance-completion-kpi',
    },
    {
      show: showKpi(AvailableKpis.maintenanceVisit),
      testId: 'maintenance-visits',
      label: 'Maintenance Visits',
      value: formatNumber(summary?.maintenanceVisit ?? null),
      href: visitTicketsPath,
      info: 'Maintenance Visit identifies how many visits an elevator received in the selected period',
    },
    {
      show: showKpi(AvailableKpis.emergencyCalls),
      testId: 'annualized-emergency-call-rate',
      label: 'Annualized Emergency Call Rate',
      value: formatDecimalStrict(summary?.emergencyCallbackRate ?? null),
      href: emergencyTicketsPath,
      info: `Emergency Call Rate identifies how many times a ${unit} breaks down each year causing an entrapment or urgent situation.`,
    },
    {
      show: showKpi(AvailableKpis.emergencyCalls),
      testId: 'emergency-calls',
      label: 'Emergency Calls',
      value: formatNumber(summary?.emergencyCalls ?? null),
      href: emergencyTicketsPath,
      info: `Emergency ${terms.callbacks} identifies how many times a ${unit} broke down causing an entrapment in the selected period`,
    },
    {
      show: showKpi(AvailableKpis.callbacks),
      testId: 'annualized-callback-rate',
      label: terms.annualizedCallbackRate,
      value: formatDecimalStrict(summary?.callbackRate ?? null),
      href: callbacksPath,
      info: `${terms.annualizedCallbackRate} identifies how many times a ${unit} breaks down each year`,
    },
    {
      show: showKpi(AvailableKpis.callbacks),
      testId: 'callbacks',
      label: terms.callbacks,
      value: formatNumber(summary?.callbacks ?? null),
      href: callbacksPath,
      info: `${terms.callbackRate} identifies how many times a ${unit} broke down in the selected period`,
    },
  ].filter(({ show }) => show);

  const row = tiles.map(({ testId, label, value, href, info, dataShot }, index) => (
    <MetricTile
      key={testId}
      testId={testId}
      label={label}
      value={value}
      href={href}
      info={info}
      dataShot={dataShot}
      size={perRow ? { xs: 12 / perRow, md: 12 / perRow } : { xs: 6, sm: 4, md: 2 }}
      // Rules separate tiles, so the first in a row has nothing to be separated from.
      // Indexed after filtering — a page showing four KPIs still wants its own first tile bare.
      divided={perRow ? index % perRow > 0 : index > 0}
    />
  ));

  // `MetricBand`'s own spacing, minus the card: the tiles carry their padding, and the
  // caller owns what they sit on.
  return bare ? (
    <Grid container spacing={{ xs: 0.5, md: 0 }}>
      {row}
    </Grid>
  ) : (
    <MetricBand>{row}</MetricBand>
  );
}
